import React from "react";
import { Box, Typography, Grid, Paper, alpha, keyframes } from '@mui/material';
import { useLanguage } from "../../i18n/LanguageContext";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import SectionContainer from "../ui/SectionContainer";

const borderRotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Blog = ({ hideHeader = false }) => {
  const { t, lang } = useLanguage();

  const values = [
    {
      id: 1,
      title: t('values.vision.title'),
      desc: t('values.vision.desc'),
      icon: <VisibilityOutlinedIcon sx={{ fontSize: 32 }} />,
      color: '#1D2558',
      delay: 100
    },
    {
      id: 2,
      title: t('values.mission.title'),
      desc: t('values.mission.desc'),
      icon: <TrackChangesIcon sx={{ fontSize: 32 }} />,
      color: '#DD8748',
      delay: 300
    },
    {
      id: 3,
      title: t('values.val.title'),
      desc: t('values.val.desc'),
      icon: <DiamondOutlinedIcon sx={{ fontSize: 32 }} />,
      color: '#2196F3',
      delay: 500
    },
  ];

  return (
    <SectionContainer
      bgColor="transparent"
      py={20}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(to bottom, #fff 0%, #f4f7fa 100%)'
      }}
    >
      {/* Decorative Circles */}
      <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, border: '2px dashed rgba(6, 57, 125, 0.05)', borderRadius: '50%', animation: `${borderRotate} 60s linear infinite` }} />

      {!hideHeader && (
        <Box textAlign="center" mb={15} data-aos="fade-up" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="overline" sx={{ color: '#DD8748', fontWeight: 900, letterSpacing: 4, mb: 2, fontSize: '1.3rem', display: 'block' }}>
            {t('values.intro')}
          </Typography>
          <Typography variant="h2" sx={{ color: '#1D2558', fontWeight: 900, mb: 3, fontSize: { xs: '2.5rem', md: '3.8rem' }, letterSpacing: -1 }}>
            {t('values.title')}
          </Typography>
          <Box sx={{ width: 100, height: 6, bgcolor: '#DD8748', mx: 'auto', borderRadius: 4 }} />
        </Box>
      )}

      <Grid container spacing={6} sx={{ position: 'relative', zIndex: 1, px: { md: 4 } }}>
        {values.map((val) => (
          <Grid item xs={12} md={4} key={val.id} data-aos="fade-up" data-aos-delay={val.delay}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                p: 6,
                borderRadius: 10,
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.04)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                boxShadow: '0 15px 40px rgba(0,0,0,0.03)',
                '&:hover': {
                  transform: 'translateY(-20px)',
                  boxShadow: `0 40px 80px ${alpha(val.color, 0.12)}`,
                  borderColor: alpha(val.color, 0.2),
                  '& .icon-wrap': {
                    bgcolor: val.color,
                    color: '#fff',
                  }
                }
              }}
            >
              <Box
                className="icon-wrap"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '20px',
                  bgcolor: alpha(val.color, 0.1),
                  color: val.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.4s ease'
                }}
              >
                {val.icon}
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 900, color: '#1D2558' }}>
                {val.title}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.9,
                  fontSize: '1.1rem',
                  '& div': { display: 'inline' }
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: val.desc }} />
              </Typography>

              {/* Artistic Background Number */}
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  right: 40,
                  fontSize: '6rem',
                  fontWeight: 950,
                  color: alpha(val.color, 0.03),
                  zIndex: 0,
                  userSelect: 'none'
                }}
              >
                0{val.id}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </SectionContainer>
  );
};

export default Blog;